// Shared option shape for every design preset. A template row stores this
// JSONB and each design reads what it needs; unknown keys are ignored so
// designs can evolve without breaking saved templates.

export type DesignKey = 'classic' | 'modern' | 'minimal';

export type LogoPosition = 'left' | 'center' | 'right' | 'hidden';

export type LinesColumn =
  | 'number'
  | 'articleCode'
  | 'description'
  | 'quantity'
  | 'unit'
  | 'unitPriceHt'
  | 'discount'
  | 'vatRate'
  | 'unitPriceTtc'
  | 'totalHt'
  | 'totalTtc';

export interface TemplateOptions {
  primaryColor?: string;      // #RRGGBB
  accentColor?: string;       // secondary color for bands/rules
  logoPosition?: LogoPosition;
  logoSizeMm?: number;        // side of the logo box, default 28

  showTitle?: boolean;        // default true
  customTitle?: string;       // overrides the doc-type title

  // Company block
  showCompanyName?: boolean;
  showCompanyTaxId?: boolean;
  showCompanyAddress?: boolean;
  showCompanyPhone?: boolean;
  showCompanyFax?: boolean;
  showCompanyEmail?: boolean;
  showCompanyTagline?: boolean;
  showCompanySubtitle?: boolean;
  showCompanyWebsite?: boolean;
  showCompanySocials?: boolean;

  // Client / supplier block
  showPartyCode?: boolean;
  showPartyName?: boolean;
  showPartyTaxId?: boolean;
  showPartyAddress?: boolean;
  showPartyCity?: boolean;
  showPartyPhone?: boolean;
  showPartyFax?: boolean;
  showPartyCarRegistration?: boolean;

  // Doc meta
  showDate?: boolean;
  showNumber?: boolean;
  showValidityDate?: boolean;  // sales quotes only

  // Lines table
  linesColumns?: LinesColumn[];
  zebraRows?: boolean;

  // Totals
  showTotalHt?: boolean;
  showTotalDiscount?: boolean;
  showTotalVat?: boolean;
  showTotalTtc?: boolean;
  showWithholding?: boolean;
  showStamp?: boolean;
  showNetToPay?: boolean;

  // VAT breakdown table (grouped by rate: Base | Taux | TVA)
  showVatBreakdown?: boolean;

  // French amount in words below totals
  showAmountInWords?: boolean;

  // Footer
  showFooter?: boolean;
  footerText?: string;         // supports {{companyName}}, {{taxId}}, {{rib}}, {{bankName}}, {{website}}
  showPageNumbers?: boolean;
  showFooterRib?: boolean;

  // Notes
  showNotes?: boolean;
}

export const DEFAULT_OPTIONS: Required<TemplateOptions> = {
  primaryColor: '#1B2A4A',
  accentColor: '#666666',
  logoPosition: 'left',
  logoSizeMm: 28,

  showTitle: true,
  customTitle: '',

  showCompanyName: true,
  showCompanyTaxId: true,
  showCompanyAddress: true,
  showCompanyPhone: true,
  showCompanyFax: true,
  showCompanyEmail: true,
  showCompanyTagline: true,
  showCompanySubtitle: true,
  showCompanyWebsite: true,
  showCompanySocials: true,

  showPartyCode: true,
  showPartyName: true,
  showPartyTaxId: true,
  showPartyAddress: true,
  showPartyCity: true,
  showPartyPhone: true,
  showPartyFax: true,
  showPartyCarRegistration: true,

  showDate: true,
  showNumber: true,
  showValidityDate: true,

  linesColumns: ['articleCode', 'description', 'quantity', 'unitPriceHt', 'vatRate', 'unitPriceTtc', 'totalHt'],
  zebraRows: true,

  showTotalHt: true,
  showTotalDiscount: true,
  showTotalVat: true,
  showTotalTtc: true,
  showWithholding: false,
  showStamp: true,
  showNetToPay: false,

  showVatBreakdown: true,
  showAmountInWords: true,

  showFooter: true,
  footerText: 'TVA : {{taxId}}   ·   RIB {{bankName}} : {{rib}}   ·   {{email}}',
  showPageNumbers: false,
  showFooterRib: true,

  showNotes: true,
};

export function mergeOptions(o?: Partial<TemplateOptions>): Required<TemplateOptions> {
  return { ...DEFAULT_OPTIONS, ...(o ?? {}) };
}
